/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { BuilderPermission } from '@gh/builder-shared-data';
import {
  FormStateSnapshot,
  getFormStateSnapshotValue,
  hasFormStateError,
  isRouteForCreationMode,
  markFormStateAsTouched,
  MutablePageState,
  UserContext,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { FormMode } from '@gh/core-ui';
import { isNotNil } from '@gh/core-util';

import { first, last, pull, sortedIndexBy } from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';

import {
  CSMasterCC,
  CSMasterCCInfo,
  CSMasterHeading,
  CSMasterHeadingInfo,
  CSMasterInfo,
  CSMasterInfoDetails,
  CSMasterItem,
  CSMasterItemInfo,
  CSMasterSelection,
  CSMasterSelectionLevel,
  CSMasterSubHeading,
  CSMasterSubHeadingInfo,
  cSMasterTreeHelper,
  getCsMasterCcCode,
  validateCSMasterCCInfo,
  validateCSMasterHeadingInfo,
  validateCSMasterInfo,
  validateCSMasterItemInfo,
  validateCSMasterSubHeadingInfo,
} from '../../resources/cs-master.model';

function addCcIntoItem(item: CSMasterItemInfo, costCenter: CSMasterCCInfo): void {
  // find position of CC by code
  const index = sortedIndexBy(
    item.children,
    costCenter,
    getCsMasterCcCode);
  item.children.splice(index, 0, costCenter);
}

@Injectable()
export class CSMasterDetailsFormService extends MutablePageState<CSMasterInfoDetails> {
  private selection$$ = new BehaviorSubject<CSMasterSelection>({});

  constructor(private route: ActivatedRoute,
              private userContext: UserContext) {
    super();
  }

  get id(): Maybe<ID> {
    return this.value.id;
  }

  get selection(): CSMasterSelection {
    return this.selection$$.value;
  }

  get selectionChanges(): Observable<CSMasterSelection> {
    return this.selection$$.asObservable();
  }

  get invalid(): boolean {
    // check validity of top form
    const { masterInfo } = this.value;
    if (hasFormStateError(masterInfo) || isNotNil(validateCSMasterInfo(getFormStateSnapshotValue(masterInfo)))) {
      return true;
    }

    // check validity of tree content
    return cSMasterTreeHelper.reduceNodes(
      this.value.headings,
      (invalid: boolean, { info }: { info: FormStateSnapshot<any> }, _1, _2, path) =>
        invalid || this.isNodeInvalid(path.length, info),
      false);
  }

  highlightAll(): void {
    // highlight top form
    const { masterInfo } = this.value;
    if (hasFormStateError(masterInfo) || validateCSMasterInfo(getFormStateSnapshotValue(masterInfo))) {
      this.value.masterInfo = markFormStateAsTouched(masterInfo);
      this.emitChange();
      return;
    }

    // find the first invalid tree node
    const invalidPath = cSMasterTreeHelper.reduceNodes(
      this.value.headings,
      (invalid, { info }: { info: FormStateSnapshot<any> }, _1, _2, path) => {
        if (invalid) {
          return invalid;
        }

        return this.isNodeInvalid(path.length, info) ? path : void 0;
      },
      <Maybe<any[]>>void 0);

    if (invalidPath) {
      const lastNode = last(invalidPath)!; // tslint:disable-line:no-non-null-assertion
      lastNode.info = markFormStateAsTouched(lastNode.info);
      this.selectByPath(invalidPath);
      this.emitChange();
    }
  }

  build(details: CSMasterInfoDetails): void {
    this.setMode(isRouteForCreationMode(this.route)
      ? FormMode.CreateMode :
      (this.userContext.hasPermission(BuilderPermission.Setup.JobAdmin.ColorSelectionMasters.EDIT)
        ? FormMode.EditMode : FormMode.ViewMode));
    this.setValue(details);

    this.selectFirstHeading();
  }

  merge(details: CSMasterInfoDetails): void {
    cSMasterTreeHelper.merge(this.value.headings, details.headings, (thisNode, anotherNode, level) => {
      thisNode.isNew = false;
      switch (level) {
        case CSMasterSelectionLevel.Heading:
          thisNode.csMasterHeadingId = anotherNode.csMasterHeadingId;
          break;
        case CSMasterSelectionLevel.SubHeading:
          thisNode.csMasterSubHeadingId = anotherNode.csMasterSubHeadingId;
          break;
        case CSMasterSelectionLevel.Item:
          thisNode.csMasterItemId = anotherNode.csMasterItemId;
          break;
        default:
          break;
      }
    });

    this.value.version = details.version;

    this.emitChange();
  }

  updateMasterInfo(masterInfo: FormStateSnapshot<CSMasterInfo>): void {
    this.value.masterInfo = masterInfo;
    this.setDirty();
    this.emitChange();
  }

  updateHeadingData(heading: CSMasterHeadingInfo, headingData: FormStateSnapshot<CSMasterHeading>): void {
    heading.info = headingData;
    this.setDirty();
    this.emitChange();
  }

  updateSubHeadingData(subHeading: CSMasterSubHeadingInfo,
                       subHeadingData: FormStateSnapshot<CSMasterSubHeading>): void {
    subHeading.info = subHeadingData;
    this.setDirty();
    this.emitChange();
  }

  updateItemData(item: CSMasterItemInfo, itemData: FormStateSnapshot<CSMasterItem>): void {
    item.info = itemData;
    this.setDirty();
    this.emitChange();
  }

  updateCCData(costCenter: CSMasterCCInfo, costCenterData: FormStateSnapshot<CSMasterCC>): void {
    // flow for updating of CC is different from other nodes,
    // because we have to guarantee order of CC codes.
    // So, we remove CC and insert it again by the new position
    const { item } = this.selection$$.value;
    pull(item!.children, costCenter); // tslint:disable-line:no-non-null-assertion
    costCenter.info = costCenterData;
    addCcIntoItem(item!, costCenter); // tslint:disable-line:no-non-null-assertion
    this.setDirty();
    this.emitChange();
  }

  addHeading(heading: CSMasterHeadingInfo): void {
    this.value.headings.unshift(heading);
    this.setDirty();
    this.emitChange();
  }

  addSubHeading(heading: CSMasterHeadingInfo, subHeading: CSMasterSubHeadingInfo): void {
    heading.children.unshift(subHeading);
    this.setDirty();
    this.emitChange();
  }

  addItem(subHeading: CSMasterSubHeadingInfo, item: CSMasterItemInfo): void {
    subHeading.children.unshift(item);
    this.setDirty();
    this.emitChange();
  }

  addCostCenters(item: CSMasterItemInfo, costCenters: CSMasterCCInfo[]): void {
    costCenters.forEach((cc) => addCcIntoItem(item, cc));
    this.setDirty();
    this.emitChange();
  }

  removeHeading(heading: CSMasterHeadingInfo): void {
    this.ensureExistingNodeSelected([heading]);

    pull(this.value.headings, heading);
    this.setDirty();
    this.emitChange();
  }

  removeSubHeading(heading: CSMasterHeadingInfo, subHeading: CSMasterSubHeadingInfo): void {
    this.ensureExistingNodeSelected([heading, subHeading]);

    pull(heading.children, subHeading);
    this.setDirty();
    this.emitChange();
  }

  removeItem(heading: CSMasterHeadingInfo, subHeading: CSMasterSubHeadingInfo, item: CSMasterItemInfo): void {
    this.ensureExistingNodeSelected([heading, subHeading, item]);

    pull(subHeading.children, item);
    this.setDirty();
    this.emitChange();
  }

  removeCC(heading: CSMasterHeadingInfo,
           subHeading: CSMasterSubHeadingInfo,
           item: CSMasterItemInfo,
           costCenter: CSMasterCCInfo): void {
    this.ensureExistingNodeSelected([heading, subHeading, item, costCenter]);

    pull(item.children, costCenter);
    this.setDirty();
    this.emitChange();
  }

  selectHeading(heading: CSMasterHeadingInfo): void {
    this.selection$$.next({
      level: CSMasterSelectionLevel.Heading,
      heading,
      subHeading: void 0,
      item: void 0,
      costCenter: void 0,
    });
  }

  selectSubHeading(heading: CSMasterHeadingInfo, subHeading: CSMasterSubHeadingInfo): void {
    this.selection$$.next({
      level: CSMasterSelectionLevel.SubHeading,
      heading,
      subHeading,
      item: void 0,
      costCenter: void 0,
    });
  }

  selectItem(heading: CSMasterHeadingInfo, subHeading: CSMasterSubHeadingInfo,
             item: CSMasterItemInfo): void {
    this.selection$$.next({
      level: CSMasterSelectionLevel.Item,
      heading,
      subHeading,
      item,
      costCenter: void 0,
    });
  }

  selectCC(heading: CSMasterHeadingInfo, subHeading: CSMasterSubHeadingInfo,
           item: CSMasterItemInfo, costCenter: CSMasterCCInfo): void {
    this.selection$$.next({
      level: CSMasterSelectionLevel.CostCenter,
      heading,
      subHeading,
      item,
      costCenter,
    });
  }

  resetSelection(): void {
    this.selection$$.next({
      level: void 0,
      heading: void 0,
      subHeading: void 0,
      item: void 0,
      costCenter: void 0,
    });
  }

  selectByPath(path: any[]): void {
    switch (path.length) {
      case CSMasterSelectionLevel.Heading:
        this.selectHeading(path[0]);
        break;
      case CSMasterSelectionLevel.SubHeading:
        this.selectSubHeading(path[0], path[1]);
        break;
      case CSMasterSelectionLevel.Item:
        this.selectItem(path[0], path[1], path[2]); // tslint:disable-line:no-magic-numbers
        break;
      case CSMasterSelectionLevel.CostCenter:
        this.selectCC(path[0], path[1], path[2], path[3]); // tslint:disable-line:no-magic-numbers
        break;
      default:
        this.resetSelection();
        break;
    }
  }

  moveNodes(): void {
    this.setDirty();
    this.emitChange();
  }

  private selectFirstHeading(): void {
    const firstHeading = first(this.value.headings);
    if (firstHeading) {
      this.selectHeading(firstHeading);
    }
  }

  private ensureExistingNodeSelected(removedPath: any[]): void {
    const { selection } = this;
    const path = cSMasterTreeHelper.findNextNode(
      this.value.headings,
      removedPath,
      cSMasterTreeHelper.createPath(selection.heading, selection.subHeading, selection.item, selection.costCenter));

    this.selectByPath(path);
  }

  private isNodeInvalid(level: CSMasterSelectionLevel, info: FormStateSnapshot<any>): boolean {
    switch (level) {
      case CSMasterSelectionLevel.Heading:
        return isNotNil(validateCSMasterHeadingInfo(getFormStateSnapshotValue(info)));
      case CSMasterSelectionLevel.SubHeading:
        return isNotNil(validateCSMasterSubHeadingInfo(getFormStateSnapshotValue(info)));
      case CSMasterSelectionLevel.Item:
        return isNotNil(validateCSMasterItemInfo(getFormStateSnapshotValue(info)));
      case CSMasterSelectionLevel.CostCenter:
        return isNotNil(validateCSMasterCCInfo(getFormStateSnapshotValue(info)));
      default:
        return false;
    }
  }
}
